import React, { useEffect, useRef, useState } from "react";

import {
  AstridDaoStoreState,
  CollateralDecimal,
  Decimal18,
  SupportedCollaterals
} from "@astrid-dao/lib-base";
import { Text, Flex, Box, Button, Card, Container, Image, Divider, useColorMode } from "theme-ui";
import { useAccount, useDisconnect } from "wagmi";

import useTokenBalance from "../../hooks/useTokenBalance";
import { fetchAtidStakingRewards } from "../api/fetchAtidStakingRewards";
import iconCoinbase from "../assets/images/icon-coinbase.svg";
import iconMetaMask from "../assets/images/icon-metamask.svg";
import iconSafe from "../assets/images/icon-safe.svg";
import iconThemeDark from "../assets/images/icon-theme-dark.svg";
import iconThemeLight from "../assets/images/icon-theme-light.svg";
import iconWalletConnect from "../assets/images/wallet-connect-logo.svg";
import { USE_TESTNET } from "../config";
import { FIAT_PRECISION, TOKEN_PRECISION } from "../constants";
import { iconCurrencyMap } from "../currencyIcons";
import { useAstridDaoSelector } from "../hooks/useAstridDaoSelector";
import { useTokenPrices } from "../hooks/useTokenPrices";
import { useAstridDao } from "../providers/AstridDaoProvider";
import {
  AccountBalanceMap,
  Currencies,
  getAccountBalancePerCollateral
} from "../rpc-contract-service/service";
import { usdCurrencyFormatter } from "../rpc-contract-service/utils/utils";
import { BASE_COLLAT, COIN, COLLAT, GT, xcCOIN } from "../strings";
import { useTheme } from "../theme";
import { getAmountToDisplay } from "../utils/digits";
import { addERC20TokenToMetamask, AddToMetamaskParams } from "../utils/metamask";
import { shortenAddress } from "../utils/shorten";
import { CollateralDecimalService } from "./Dashboard/models/decimals/CollateralDecimalService";
import { DashboardDecimal18 } from "./Dashboard/models/decimals/DashboardDecimal18";
import { Icon } from "./Icon";
import { InfoIcon } from "./InfoIcon";
import { LoadingOverlay } from "./LoadingOverlay";
import SvgIcon, { IconDisconnectedWallet } from "./SvgIcon";
import { Tooltip } from "./Tooltip";

const select = ({
  accountBalance,
  baiBalance,
  xcBaiBalance,
  atidBalance,
  wrappedTokenBalance
}: AstridDaoStoreState) => ({
  accountBalance,
  baiBalance,
  xcBaiBalance,
  atidBalance,
  wrappedTokenBalance
});

const walletAssetsByOrder = [
  "BAI",
  "xcBAI",
  "ASTR",
  "WASTR",
  "ATID",
  "WBTC",
  "WETH",
  "ceUSDC",
  "ceUSDT",
  "DOT",
  "DAI",
  "BUSD"
];

// const itemWidth = "324px";
const itemWidth = "100%";

const SHORTENED_AMOUNT_THRESHOLD = 6;

export const BAI_TOKEN_IMAGE_URL =
  "https://raw.githubusercontent.com/AstridDAO/media-kit/ebf9d00f2cbd6cd1d582feb7728da55dde508a10/bai-and-atid-token-logos/bai_icon_light.svg";

export const xcBAI_TOKEN_IMAGE_URL =
  "https://raw.githubusercontent.com/AstridDAO/media-kit/93f4afca2fe55abba3d7d758f8e320f47538d343/bai-and-atid-token-logos/xcBAI_logo_light.svg";

export const ATID_TOKEN_IMAGE_URL =
  "https://raw.githubusercontent.com/AstridDAO/media-kit/ebf9d00f2cbd6cd1d582feb7728da55dde508a10/bai-and-atid-token-logos/atid_icon_light.svg";
const DEFAULT_TOKEN_PRECISION = 18;

const wrapTokenName = (token: string) => {
  if (token === "ceUSDT") {
    return "USDT";
  } else if (token === "ceUSDC" && !USE_TESTNET) {
    return "USDC";
  }
  return token;
};

export const Balance: React.FC<{
  showWalletAddress?: boolean;
  ignoreClick?: boolean;
  mini?: boolean;
}> = ({ showWalletAddress, ignoreClick, mini }) => {
  const { theme } = useTheme();
  const [colorMode, setColorMode] = useColorMode();
  const [isShowMore, setIsShowMore] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | undefined>();
  const { account: address, contractsConfig, deactivate, isTestnet } = useAstridDao();
  const { connector } = useAccount();
  const { disconnect } = useDisconnect({
    onSuccess(context) {
      console.log("Disconnected", context);
      deactivate();
    },
    onError(error, context) {
      console.log("Disconnect error", error, context);
      deactivate();
    }
  });
  const tokenPricePerCollateral = useTokenPrices();
  const { accountBalance, baiBalance, xcBaiBalance, atidBalance, wrappedTokenBalance } =
    useAstridDaoSelector(select);
  const [isUserAccountOpen, setUserAccountOpen] = useState(false);
  const [atidStakingRewards, setAtidStakingRewards] = useState<number | undefined>(undefined);
  const [accountBalances, setAccountBalances] = useState<AccountBalanceMap | undefined>();
  const [walletIcon, setWalletIcon] = useState<string>(iconMetaMask);
  const erc20TokenParams = (contractsConfig?.collaterals ?? [])
    .map(collateralContracts => {
      const { name, address } = collateralContracts;
      const key = name as SupportedCollaterals;

      return [
        name,
        {
          tokenSymbol: name,
          tokenAddress: address,
          tokenPrecision: CollateralDecimalService.getPrecisionByCollateralType(key),
          tokenImage: ""
        }
      ];
    })
    .concat([
      [
        "BAI",
        {
          tokenSymbol: "BAI",
          tokenAddress: contractsConfig.baseContracts.baiToken,
          tokenPrecision: DEFAULT_TOKEN_PRECISION,
          tokenImage: BAI_TOKEN_IMAGE_URL
        }
      ],
      [
        "xcBAI",
        {
          tokenSymbol: "xcBAI",
          tokenAddress: contractsConfig.baseContracts.xcBaiToken,
          tokenPrecision: DEFAULT_TOKEN_PRECISION,
          tokenImage: xcBAI_TOKEN_IMAGE_URL
        }
      ],
      [
        "ATID",
        {
          tokenSymbol: "ATID",
          tokenAddress: contractsConfig.baseContracts.atidToken,
          tokenPrecision: DEFAULT_TOKEN_PRECISION,
          tokenImage: ATID_TOKEN_IMAGE_URL
        }
      ]
    ]) as [[Currencies, AddToMetamaskParams]];

  const erc20TokenToMetamaskMap = new Map<Currencies, AddToMetamaskParams>(erc20TokenParams);

  useEffect(() => {
    if (isUserAccountOpen) {
      (async () => {
        try {
          const result = await fetchAtidStakingRewards(address);
          setAtidStakingRewards(result);
        } catch (error) {
          console.error("Looks like there was a problem: ", error);
        }
      })();

      getAccountBalancePerCollateral(address, COLLAT()).then(setAccountBalances);
    }
  }, [isUserAccountOpen, address]);

  const overlayRef = useRef<HTMLDivElement>(null);

  const atidStakingTentativeRewardFromApi = Number(atidStakingRewards ?? 0) / 10 ** 18;
  const decimalifiedAtidStakingTentativeReward = Decimal18.from(
    Number(atidStakingTentativeRewardFromApi)
  );
  const atidPrice = Decimal18.from(tokenPricePerCollateral?.get?.("ATID")?.toString() ?? 0);
  const airdropValue = decimalifiedAtidStakingTentativeReward.mul(atidPrice);
  const [tentativeAtidStakingReward, shouldShowTentativeAtidStakingRewardTooltip] =
    getAmountToDisplay(decimalifiedAtidStakingTentativeReward, SHORTENED_AMOUNT_THRESHOLD);

  const [copied, setCopied] = useState<string>();

  useEffect(() => {
    if (copied !== undefined) {
      let cancelled = false;

      setTimeout(() => {
        if (!cancelled) {
          setCopied(undefined);
        }
      }, 2000);

      return () => {
        cancelled = true;
      };
    }
  }, [copied]);

  useEffect(() => {
    switch (connector?.id) {
      case "walletConnect":
        setWalletIcon(iconWalletConnect);
        break;
      case "safe":
        setWalletIcon(iconSafe);
        break;
      case "coinbaseWallet":
        setWalletIcon(iconCoinbase);
        break;
      default:
        setWalletIcon(iconMetaMask);
    }
  }, [connector]);

  const userAccounts: [string, Decimal18 | CollateralDecimal | DashboardDecimal18][] = [
    [BASE_COLLAT, accountBalance],
    [COLLAT(), wrappedTokenBalance],
    [GT, atidBalance],
    [COIN, baiBalance],
    [xcCOIN, xcBaiBalance]
  ];

  accountBalances?.forEach((value, key) => {
    userAccounts.push([key, value]);
  });

  if (accountBalances?.size) {
    userAccounts.sort(([a], [b]) => {
      return walletAssetsByOrder.indexOf([a][0]) - walletAssetsByOrder.indexOf([b][0]);
    });
  }

  const wrappedTokenBalanceToDisplay = useTokenBalance(COLLAT() as SupportedCollaterals);
  const customBoundary = useRef<HTMLInputElement>(null);

  return (
    <Flex
      sx={{
        justifyContent: "flex-end",
        alignItems: "center",
        overflow: "hidden"
      }}
    >
      <Flex
        sx={{
          justifyContent: "center",
          alignItems: "center",
          background: "G08",
          px: 3,
          height: "40px",
          ml: "10px",
          borderRadius: "16px",
          cursor: ignoreClick ? "default" : "pointer",
          ":hover": {
            background: ignoreClick ? "G08" : "G40"
          }
        }}
        onClick={() =>
          ignoreClick ? console.log("do nothing") : setUserAccountOpen(!isUserAccountOpen)
        }
      >
        <Image
          width={24}
          height={24}
          src={showWalletAddress ? walletIcon : iconCurrencyMap[COLLAT() as SupportedCollaterals]}
        />
        <Text
          variant="mediumH3"
          sx={{
            color: "B100",
            ml: "6px"
          }}
        >
          {showWalletAddress
            ? mini
              ? address.slice(-4)
              : address.slice(0, 6) + "..." + address.slice(-4)
            : wrappedTokenBalanceToDisplay.prettify(TOKEN_PRECISION)}
        </Text>
        {!showWalletAddress && (
          <Text
            variant="mediumH3"
            sx={{
              color: "B100",
              ml: "6px"
            }}
          >
            {COLLAT()}
          </Text>
        )}
      </Flex>

      {isUserAccountOpen && (
        <Container
          variant="infoOverlay"
          ref={overlayRef}
          onClick={e => {
            if (e.target === overlayRef.current) {
              setUserAccountOpen(false);
            }
          }}
          sx={{
            bg: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(15px)"
          }}
        >
          <Card ref={customBoundary} variant="wallet">
            <Flex
              sx={{
                width: itemWidth,
                height: "auto",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                pl: 3
              }}
            >
              <Text variant="text.msTitle20" sx={{ color: "B100" }}>
                Wallet
              </Text>

              <Button
                variant="icon"
                sx={{ width: "40px", height: "40px" }}
                onClick={() => setUserAccountOpen(false)}
              >
                <Icon name="times" size="xl" color={theme.colors.B40} />
              </Button>
            </Flex>

            <Flex
              sx={{
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "space-between",
                mt: 6,
                pl: 3,
                width: itemWidth
              }}
            >
              <Text variant="text.mediumH2" sx={{ color: "B100" }}>
                {shortenAddress(address)}
              </Text>
              <Tooltip message={copied ? "copied" : "copy"} placement="top">
                <Button
                  variant="icon"
                  sx={{ width: "40px", height: "40px" }}
                  onClick={() => {
                    navigator.clipboard.writeText(address);
                    setCopied(address);
                  }}
                >
                  <Icon
                    prefix={copied === address ? "fas" : "far"}
                    name={copied === address ? "clipboard-check" : "copy"}
                    size="1x"
                    color={theme.colors.B40}
                  />
                </Button>
              </Tooltip>

              <Tooltip message="Disconnect Wallet" placement="top">
                <SvgIcon
                  onClick={() => {
                    // may cause a error
                    // https://github.com/WalletConnect/walletconnect-monorepo/issues/1772
                    try {
                      disconnect();
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                  color={theme.colors.B40}
                  icon={IconDisconnectedWallet}
                />
              </Tooltip>
            </Flex>

            <Flex
              sx={{
                mt: 5,
                alignItems: "flex-start",
                flexDirection: "column",
                width: itemWidth
              }}
            >
              <Box sx={{ pl: 3 }}>
                <Text
                  variant="text.mediumH4"
                  sx={{
                    color: "B100"
                  }}
                >
                  Wallet Balance
                </Text>
              </Box>

              <Box
                sx={{
                  overflowY: "scroll",
                  height: "auto",
                  maxHeight: ["100%", "350px", "350px"],
                  scrollbarWidth: "thin",
                  "::-webkit-scrollbar": {
                    width: "20px"
                  }
                }}
              >
                {(isShowMore ? userAccounts : userAccounts.slice(0, 4))
                  .filter(item => {
                    // hide xcBAI
                    if (isTestnet) {
                      return true;
                    } else {
                      return item[0] !== "xcBAI";
                    }
                  })
                  .map(([currency, balance], i) => {
                    const [balanceToDisplay, shouldShowTooltip] = getAmountToDisplay(
                      balance,
                      SHORTENED_AMOUNT_THRESHOLD
                    );
                    const key = currency === BASE_COLLAT ? "WASTR" : (currency as Currencies);
                    const tokenValueInUsd =
                      Number(tokenPricePerCollateral.get(key)?.toString()) *
                      Number(balance.toString());
                    const formattedTokenValueInUsd = usdCurrencyFormatter.format(tokenValueInUsd);
                    const tooltip = (balance ?? Decimal18.ZERO).prettify();

                    return (
                      <Container
                        key={`${i}-${currency}`}
                        variant="wallet-balance"
                        onMouseEnter={() => {
                          setHoveredItem(currency);
                        }}
                        onMouseLeave={() => {
                          setHoveredItem(undefined);
                        }}
                      >
                        <Flex
                          sx={{
                            justifyContent: "flex-start",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "12px",
                            width: "100%"
                            // width: "105px"
                          }}
                        >
                          <Image
                            loading="eager"
                            width="32px"
                            height="32px"
                            alt={`${currency} Logo`}
                            src={iconCurrencyMap[currency as SupportedCollaterals]}
                          />
                          <Text
                            variant="text.mediumH3"
                            sx={{
                              color: "B100"
                            }}
                          >
                            {currency}
                          </Text>
                        </Flex>

                        {hoveredItem === currency && currency !== "ASTR" ? (
                          <Flex sx={{ flexDirection: "row", alignItems: "center" }}>
                            <Text variant="text.regularBodySmall" sx={{ color: "B40" }}>
                              Add token to wallet
                            </Text>
                            <Button
                              sx={{ ml: 2 }}
                              variant="icon"
                              onClick={() => {
                                const params = erc20TokenToMetamaskMap.get(currency as Currencies)!;
                                const newParams = {
                                  ...params,
                                  tokenSymbol: wrapTokenName(params.tokenSymbol)
                                };
                                console.log(params, newParams);
                                return addERC20TokenToMetamask(newParams);
                              }}
                            >
                              {/* <Tooltip
                                placement="top"
                                strategy="fixed"
                                message={`Add ${currency} to Metamask`}
                              >
                                <span> */}
                              <Icon
                                name={"plus"}
                                size="lg"
                                color={theme.colors.B40}
                                hoveredColor={theme.colors.B100}
                              />
                              {/* </span> */}
                              {/* </Tooltip> */}
                            </Button>
                          </Flex>
                        ) : (
                          <Flex
                            sx={{
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              p: 0,
                              m: 0
                            }}
                          >
                            <Flex
                              sx={{
                                flexDirection: "column",
                                alignItems: "flex-end",
                                justifyContent: "center",
                                p: 0,
                                gap: 1,
                                height: "38px",
                                maxWidth: "auto",
                                fontSize: 3,
                                fontWeight: "medium",
                                lineHeight: "19px"
                              }}
                            >
                              <Flex>
                                <Text variant="text.mediumBodyLarge" sx={{ color: "B100" }}>
                                  {balanceToDisplay}
                                </Text>
                                {tooltip && shouldShowTooltip && (
                                  <InfoIcon
                                    tooltipIcon="magnifying-glass-dollar"
                                    size="xs"
                                    color="rgba(255, 255, 255, 0.65)"
                                    content={tooltip}
                                  />
                                )}
                              </Flex>
                              <Text variant="text.regularBodySmall" sx={{ color: "B40" }}>
                                {formattedTokenValueInUsd}
                              </Text>
                            </Flex>
                          </Flex>
                        )}
                      </Container>
                    );
                  })}
              </Box>
              <Flex
                sx={{
                  width: "100%",
                  height: "40px",
                  "&:hover": {
                    bg: "G08"
                  },
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  mt: 2,
                  borderRadius: "8px"
                }}
                onClick={() => setIsShowMore(!isShowMore)}
              >
                <Icon
                  name={isShowMore ? "chevron-up" : "chevron-down"}
                  size="xl"
                  color={theme.colors.B40}
                />
              </Flex>
            </Flex>

            <Divider
              sx={{
                mt: 2,
                mx: 3,
                bg: "G12",
                height: "1px",
                width: "calc(100% - 24px)",
                display: ["block", "block", "none"]
              }}
            />

            {/* hide this function due to https://astrid-tech.slack.com/archives/C0362HMHKS6/p1694483150198619?thread_ts=1694405830.248139&cid=C0362HMHKS6 */}
            {/* <Flex
              sx={{
                gap: "8px",
                mt: 3,
                width: itemWidth,
                pl: 3,
                alignItems: "flex-start",
                flexDirection: "column"
              }}
            >
              <Flex
                sx={{
                  alignItems: "flex-start",
                  flexDirection: "row"
                }}
              >
                <Text
                  variant="text.mediumH4"
                  sx={{
                    color: "B100"
                  }}
                >
                  My Estimated ATID Airdrops
                </Text>
              </Flex>
              <Flex
                sx={{
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 2
                }}
              >
                <Image
                  loading="eager"
                  width="32px"
                  height="32px"
                  alt={`${GT} Logo`}
                  src={iconCurrencyMap[GT as SupportedCollaterals]}
                />
                <Box sx={{ flex: 1, ml: 3 }}>
                  <Text
                    variant="text.mediumBodyLarge"
                    sx={{ color: "B100" }}
                  >{`${GT} Staking`}</Text>
                </Box>
                <Flex sx={{ flex: 1, flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
                  <Text sx={{ color: "B100" }}>{tentativeAtidStakingReward} </Text>
                  <Text variant="text.regularBodySmall" sx={{ color: "B40" }}>
                    ${airdropValue.prettify(FIAT_PRECISION)}
                  </Text>
                </Flex>
              </Flex>
            </Flex> */}

            <Flex
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                height: "40px",
                alignItems: "center",
                mt: [3, 3, 6],
                width: "100%",
                pl: 3,
                display: ["flex", "flex", "none"]
              }}
            >
              <Text variant="text.mediumH3" sx={{ color: "B100" }}>
                Switch theme
              </Text>
              <Flex
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  background: "G08",
                  width: "40px",
                  height: "40px",
                  ml: "10px",
                  borderRadius: "16px",
                  cursor: "pointer",
                  ":hover": {
                    background: "G40"
                  }
                }}
              >
                <Image
                  src={colorMode === "light" ? iconThemeLight : iconThemeDark}
                  sx={{
                    width: "24px",
                    height: "24px"
                  }}
                  onClick={() => {
                    // change color mode to next color mode
                    setColorMode(colorMode === "light" ? "dark" : "light");
                  }}
                />
              </Flex>
            </Flex>

            {!accountBalances?.size && <LoadingOverlay />}
          </Card>
        </Container>
      )}
    </Flex>
  );
};
